import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MessageSquare, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
const contactSchema = z.object({
  name: z.string().min(2, "Le nom est trop court"),
  email: z.string().email("Format d'email invalide"),
  service: z.string().min(1, "Veuillez choisir un service"),
  message: z.string().min(10, "Veuillez détailler un peu plus votre projet"),
});
type ContactFormData = z.infer<typeof contactSchema>;
export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [checkingWhatsapp, setCheckingWhatsapp] = useState(true);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      service: ""
    }
  });
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    async function checkWhatsappStatus() {
      try {
        const response = await fetch('/api/integrations/whatsapp/status', {
          signal: controller.signal
        });
        if (!response.ok) {
           throw new Error('Status check failed');
        }
        const data = await response.json();
        if (isMounted) {
          setWhatsappConnected(data?.status === 'connected');
        }
      } catch (err) {
        // Silent fallback for production stability
        if (isMounted) {
          setWhatsappConnected(false);
        }
      } finally {
        if (isMounted) {
          setCheckingWhatsapp(false);
        }
      }
    }
    checkWhatsappStatus();
    // Safety timeout to ensure spinner doesn't stay forever
    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setCheckingWhatsapp(false);
      }
    }, 5000);
    return () => { 
      isMounted = false;
      controller.abort();
      clearTimeout(safetyTimer);
    };
  }, []);
  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        toast.success("Demande envoyée !", {
          description: "Notre équipe vous recontactera dans les plus brefs délais.",
        });
        reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error("Oups ! Une erreur est survenue", {
        description: "Veuillez réessayer ou nous contacter directement via WhatsApp.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleWhatsappClick = useCallback(() => {
    if (checkingWhatsapp) return;
    if (!whatsappConnected) {
      toast.error("Service WhatsApp momentanément indisponible", {
        description: "Veuillez utiliser le formulaire ou nous envoyer un email.",
      });
      return;
    }
    window.open('https://wa.me/221782412211', '_blank');
  }, [whatsappConnected, checkingWhatsapp]);
  return (
    <section id="contact" className="py-24 bg-brand-purple text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-lime opacity-[0.05] rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-[0.03] rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-brand-lime font-bold tracking-widest uppercase text-sm mb-4">Contact</h2>
            <h3 className="text-4xl md:text-5xl font-display font-bold mb-8 leading-tight">
              Prêt à lancer votre <br /><span className="text-brand-lime">prochain grand projet ?</span>
            </h3>
            <p className="text-lg text-white/70 mb-12 max-w-lg leading-relaxed text-pretty">
              Discutons de vos objectifs et de la manière dont nous pouvons transformer votre présence digitale en véritable levier de croissance.
            </p>
            <div className="space-y-8">
              <a href="tel:+221782412211" className="flex items-center gap-5 group w-fit">
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-brand-lime group-hover:text-brand-purple transition-all duration-300 shadow-sm">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs text-white/50 font-bold uppercase tracking-wider">Téléphone</div>
                  <div className="text-xl font-medium">+221 78 241 22 11</div>
                </div>
              </a>
              <a href="mailto:khalilcosenegal@gmail.com" className="flex items-center gap-5 group w-fit">
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-brand-lime group-hover:text-brand-purple transition-all duration-300 shadow-sm">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs text-white/50 font-bold uppercase tracking-wider">Email</div>
                  <div className="text-xl font-medium">khalilcosenegal@gmail.com</div>
                </div>
              </a>
              <button
                onClick={handleWhatsappClick}
                disabled={checkingWhatsapp}
                className="flex items-center gap-5 group text-left w-fit disabled:opacity-50"
              >
                <div className={`w-14 h-14 rounded-full bg-white/10 flex items-center justify-center transition-all duration-300 shadow-sm ${whatsappConnected ? 'group-hover:bg-green-500 group-hover:text-white' : ''}`}>
                  {checkingWhatsapp ? <Loader2 className="w-6 h-6 animate-spin" /> : <MessageSquare className="w-6 h-6" />}
                </div>
                <div>
                  <div className="text-xs text-white/50 flex items-center gap-2 font-bold uppercase tracking-wider">
                    WhatsApp
                    {!checkingWhatsapp && whatsappConnected && (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle2 className="w-3 h-3" /> En ligne
                      </span>
                    )}
                    {!checkingWhatsapp && !whatsappConnected && (
                      <span className="flex items-center gap-1 text-red-400">
                        <AlertCircle className="w-3 h-3" /> Offline
                      </span>
                    )}
                  </div>
                  <div className="text-xl font-medium">Discutez en direct</div>
                </div>
              </button>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-sm p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70 ml-1">Nom complet</label>
                  <Input
                    {...register("name")}
                    placeholder="John Doe"
                    className={`h-12 bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-brand-lime rounded-xl ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1 px-1">{errors.name.message}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/70 ml-1">Email professionnel</label>
                  <Input
                    {...register("email")}
                    type="email"
                    placeholder="john@example.com"
                    className={`h-12 bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-brand-lime rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1 px-1">{errors.email.message}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/70 ml-1">Service souhaité</label>
                <select
                  {...register("service")}
                  className={`w-full h-12 px-4 rounded-xl bg-white/10 border border-white/10 text-white focus:outline-none focus:ring-1 focus:ring-brand-lime appearance-none ${errors.service ? 'border-red-500' : ''}`}
                >
                  <option value="" disabled className="bg-brand-purple">Choisir un service</option>
                  <option value="social" className="bg-brand-purple">Community Management</option>
                  <option value="web" className="bg-brand-purple">Création de Site</option>
                  <option value="branding" className="bg-brand-purple">Branding</option>
                  <option value="marketing" className="bg-brand-purple">Marketing Digital</option>
                </select>
                {errors.service && <p className="text-red-400 text-xs mt-1 px-1">{errors.service.message}</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/70 ml-1">Votre projet en quelques mots</label>
                <Textarea
                  {...register("message")}
                  placeholder="Expliquez-nous vos objectifs..."
                  className={`bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-brand-lime min-h-[140px] rounded-xl ${errors.message ? 'border-red-500' : ''}`}
                />
                {errors.message && <p className="text-red-400 text-xs mt-1 px-1">{errors.message.message}</p>}
              </div>
              <Button
                type="submit"
                variant="lime"
                size="xl"
                className="w-full h-14 rounded-xl text-lg font-bold group"
                loading={isSubmitting}
              >
                Envoyer ma demande
                {!isSubmitting && <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
              </Button>
              <p className="text-[10px] text-center text-white/40 uppercase tracking-widest font-medium">
                Réponse garantie sous 24h ouvrées
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}